import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { X, MapPin, ChevronLeft, ChevronRight, Images } from "lucide-react";
import { API, CATEGORIES, photoSrc } from "@/lib/siteData";

const filters = [
  { label: "Tous les chantiers", value: "all", testId: "gallery-filter-all" },
  { label: "Peinture Intérieure", value: "Peinture Intérieure", testId: "gallery-filter-peinture" },
  { label: "Rénovation Complète", value: "Rénovation Complète", testId: "gallery-filter-renovation" },
  { label: "Ravalement Extérieur", value: "Ravalement Extérieur", testId: "gallery-filter-ravalement" },
  { label: "Boiseries & Laques", value: "Boiseries & Laques", testId: "gallery-filter-boiseries" },
  { label: "Sols Béton", value: "Sols Béton", testId: "gallery-filter-sols-beton" },
];

const spans = ["lg:col-span-7 lg:row-span-2", "lg:col-span-5", "lg:col-span-5", "lg:col-span-4", "lg:col-span-4", "lg:col-span-4"];

export const ChantierGallery = () => {
  const [photos, setPhotos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/photos`)
      .then((res) => setPhotos(res.data))
      .catch(() => setPhotos([]));
  }, []);

  const visible = filter === "all" ? photos : photos.filter((p) => p.category === filter);

  const items = [];
  const albumMap = {};
  for (const p of visible) {
    if (p.album) {
      if (!albumMap[p.album]) {
        albumMap[p.album] = [];
        items.push(albumMap[p.album]);
      }
      albumMap[p.album].push(p);
    } else {
      items.push([p]);
    }
  }

  return (
    <section id="galerie" className="py-28 lg:py-36 bg-[#F3EFE8]" data-testid="gallery-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-3xl mb-14"
        >
          <p className="font-mono-tech text-xs uppercase tracking-[0.25em] text-[#9E5A38] mb-5">
            Portfolio & archive visuelle
          </p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight leading-[1.15] text-[#1C1815]">
            Galerie de chantiers récents
          </h2>
          <p className="mt-6 text-base sm:text-lg font-light leading-relaxed text-[#59524A]">
            Chaque réalisation est documentée avec précision. Inspectez la
            perfection des angles, de la planimétrie et de la saturation chromatique.
          </p>
        </motion.div>

        <div className="flex flex-wrap gap-2.5 mb-12">
          {filters.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              data-testid={f.testId}
              className={`rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 border ${
                filter === f.value
                  ? "bg-[#1A1714] text-[#FAF7F2] border-[#1A1714]"
                  : "bg-transparent text-[#574E45] border-[#1A1714]/15 hover:border-[#9E5A38] hover:text-[#9E5A38]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {items.length === 0 ? (
          <p className="text-[#8A7E72] text-sm py-16" data-testid="gallery-empty-state">
            Aucun chantier dans cette catégorie pour le moment.
          </p>
        ) : (
          <div className="grid lg:grid-cols-12 auto-rows-[260px] gap-5">
            {items.map((group, i) => {
              const p = group[0];
              const cover = group[group.length - 1];
              const isAlbum = group.length > 1;
              const displayTitle = isAlbum ? p.album : p.title;
              return (
              <motion.button
                key={isAlbum ? p.album : p.id}
                layout
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (i % 3) * 0.1 }}
                onClick={() => setLightbox({ photos: group, index: 0 })}
                data-testid={`gallery-photo-card-${i}`}
                className={`group relative overflow-hidden rounded-sm border border-[#1A1714]/10 text-left ${spans[i % spans.length]} col-span-1`}
              >
                <img
                  src={photoSrc(cover)}
                  alt={displayTitle}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1714]/75 via-[#1A1714]/10 to-transparent opacity-80 group-hover:opacity-95 transition-opacity duration-500" />
                {isAlbum && (
                  <div
                    className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-[#1A1714]/70 backdrop-blur-md text-[#FAF7F2] rounded-full px-3.5 py-1.5 font-mono-tech text-[10px] uppercase tracking-wider"
                    data-testid={`gallery-album-badge-${i}`}
                  >
                    <Images className="w-3.5 h-3.5" /> {group.length} photos
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#BFA574] mb-2">
                    {p.category}
                  </p>
                  <h3 className="font-serif-display text-xl lg:text-2xl font-medium text-[#FAF7F2] leading-snug">
                    {displayTitle}
                  </h3>
                  {(p.location || p.year) && (
                    <p className="mt-2 flex items-center gap-1.5 font-mono-tech text-[11px] text-[#FAF7F2]/70">
                      <MapPin className="w-3 h-3" />
                      {[p.location, p.year].filter(Boolean).join(" — ")}
                    </p>
                  )}
                </div>
              </motion.button>
              );
            })}
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightbox && (() => {
          const current = lightbox.photos[lightbox.index];
          const total = lightbox.photos.length;
          const go = (dir) =>
            setLightbox((lb) => ({ ...lb, index: (lb.index + dir + total) % total }));
          return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-[#1A1714]/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox-modal"
          >
            <motion.figure
              initial={{ scale: 0.94, y: 24 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 12 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-5xl w-full relative"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                key={current.id}
                src={photoSrc(current)}
                alt={current.title}
                className="w-full max-h-[70vh] object-contain rounded-sm"
              />
              {total > 1 && (
                <>
                  <button
                    onClick={() => go(-1)}
                    data-testid="gallery-lightbox-prev"
                    aria-label="Photo précédente"
                    className="absolute left-3 top-[35%] -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#1A1714]/70 border border-[#FAF7F2]/25 text-[#FAF7F2] hover:bg-[#9E5A38] hover:border-[#9E5A38] transition-colors duration-300"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => go(1)}
                    data-testid="gallery-lightbox-next"
                    aria-label="Photo suivante"
                    className="absolute right-3 top-[35%] -translate-y-1/2 inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#1A1714]/70 border border-[#FAF7F2]/25 text-[#FAF7F2] hover:bg-[#9E5A38] hover:border-[#9E5A38] transition-colors duration-300"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}
              <figcaption className="mt-5 flex items-start justify-between gap-6">
                <div>
                  <h3 className="font-serif-display text-2xl text-[#FAF7F2]">{current.title}</h3>
                  <p className="mt-1.5 text-sm text-[#FAF7F2]/70 font-light">{current.description}</p>
                  {total > 1 && (
                    <p className="mt-2 font-mono-tech text-[11px] uppercase tracking-wider text-[#BFA574]" data-testid="gallery-lightbox-counter">
                      Photo {lightbox.index + 1} / {total}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => setLightbox(null)}
                  data-testid="gallery-lightbox-close"
                  aria-label="Fermer"
                  className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-full border border-[#FAF7F2]/25 text-[#FAF7F2] hover:bg-[#9E5A38] hover:border-[#9E5A38] transition-colors duration-300"
                >
                  <X className="w-5 h-5" />
                </button>
              </figcaption>
            </motion.figure>
          </motion.div>
          );
        })()}
      </AnimatePresence>
    </section>
  );
};
